import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { auth, db } from "../db/firebase";
import { NO_ACCESS, resolveUserAccess, UserAccess, UserRole } from "../utils/resolveUserAccess";

interface AuthContextValue extends UserAccess {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [access, setAccess] = useState<UserAccess>(NO_ACCESS);
  const [loading, setLoading] = useState(true);
  const languageAppliedFor = useRef<string | null>(null);

  const applyOrganizationLanguage = useCallback(
    async (organizationId: string | null) => {
      if (!organizationId || languageAppliedFor.current === organizationId) return;
      languageAppliedFor.current = organizationId;

      try {
        const orgSnap = await getDoc(doc(db, "organizations", organizationId));
        if (!orgSnap.exists()) return;
        startTransition(() => {
          i18n.changeLanguage(orgSnap.data().language || "en");
        });
      } catch {
        /* language is cosmetic — never block sign-in on it */
      }
    },
    [i18n]
  );

  const loadAccess = useCallback(
    async (currentUser: User | null) => {
      if (!currentUser) {
        languageAppliedFor.current = null;
        setAccess(NO_ACCESS);
        return;
      }

      try {
        const resolved = await resolveUserAccess(currentUser.uid);
        setAccess(resolved);
        await applyOrganizationLanguage(resolved.organizationId);
      } catch {
        setAccess(NO_ACCESS);
      }
    },
    [applyOrganizationLanguage]
  );

  useEffect(() => {
    let settled = false;
    // Safety net: never leave the app on the auth skeleton forever (iOS Safari
    // can hang Firestore reads; dashboard pages handle missing access separately).
    const safety = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        setLoading(false);
      }
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      try {
        await loadAccess(currentUser);
      } finally {
        if (!settled) {
          settled = true;
          window.clearTimeout(safety);
          setLoading(false);
        }
      }
    });

    return () => {
      window.clearTimeout(safety);
      unsubscribe();
    };
  }, [loadAccess]);

  const refresh = useCallback(() => loadAccess(auth.currentUser), [loadAccess]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, refresh, ...access }),
    [user, loading, refresh, access]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}

export type { UserRole };
